/**
 * @module linkage/qbFields/useQBCategoryPartials
 * @description 按 QB 类别计算 partial diff，供 useQBLinkagePipeline 合并后跑联动 handler
 *
 * 维/指/raw 列列表必须使用 useQueryColumnDimensions / useQueryColumnMetrics /
 * useQueryColumnRawColumns：model 重命名原地 mutate 时，通用 useQuerySelector + shallowEqual
 * 检测不到 column_display 变化，会导致 rename 漏检、field_setting 等无法 migrate。
 *
 * ## prev 基线 vs useMemo 缓存（必读）
 *
 * - **prevSnapshotRef**：pipeline 每次跑完由 `commitPrevSnapshot` 冻结的「上次 QB」快照，供 diff 的 prev 端使用。
 * - **各类 partial 的 useMemo**：deps 主要为 DVA 字段（如 `columnMetrics`）；**commit 只改 ref，不会触发 useMemo 重算**。
 * - 若 deps 不含 `prevVersion`：清空后 commit 虽把 prev 更新为 `[]`，但 `columnMetrics` 不变时仍缓存
 *   `{ metricRemoved: [...] }`，后续拖维度 / Confirm 会误合并过期 diff。
 * - **prevVersion**：commit 时递增，强制 useMemo 用最新 prev 重算；对齐后无变化的类别应变为 `UNCHANGED`。
 *
 * @see hooks/dva/useQueryColumnLists.ts
 */

import { useMemo, useRef, useState } from 'react';
import type { DependencyList, MutableRefObject } from 'react';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import { detectDateDimensionsInList } from '../../manifest/dimensionDateUtils';
import { useQueryColumnDimensions, useQueryColumnMetrics, useQueryColumnRawColumns, useQuerySelector } from '../../hooks/deps';
import {
  detectCompareTimeChanges,
  detectDimensionChanges,
  detectMetricChanges,
  detectRawColumnChanges,
  detectUserFilterChanges,
  isCategoryChanged,
} from './qbLinkage/detectChanges/index';
import type { IPartialQBChangeSet } from './qbLinkage/detectChanges/partialChangeSet';
import { freezeQBSnapshot } from './qbLinkage/fieldSnapshot';
import type { IQBSnapshot, TQBCategory, TQBMode } from './qbLinkage/types';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';

/** 该类别相对 prev 无变化（稳定引用） */
const QB_CATEGORY_UNCHANGED = Symbol('QB_CATEGORY_UNCHANGED');

type TCategoryPartialResult =
  | IPartialQBChangeSet
  | typeof QB_CATEGORY_UNCHANGED
  | null;

export interface IUseQBCategoryPartialsResult {
  nextSnapshot: IQBSnapshot;
  categories: Set<TQBCategory>;
  partials: IPartialQBChangeSet[];
  commitPrevSnapshot: () => void;
}

function computeCategoryPartial(
  category: TQBCategory,
  prev: IQBSnapshot,
  next: IQBSnapshot,
  detect: (prev: IQBSnapshot, next: IQBSnapshot) => IPartialQBChangeSet,
): TCategoryPartialResult {
  if (!isCategoryChanged(category, prev, next)) {
    return QB_CATEGORY_UNCHANGED;
  }
  return detect(prev, next);
}

function mergeCategoryPartials(
  entries: [TQBCategory, TCategoryPartialResult][],
): { categories: Set<TQBCategory>; partials: IPartialQBChangeSet[] } {
  const categories = new Set<TQBCategory>();
  const partials: IPartialQBChangeSet[] = [];

  for (const [category, partial] of entries) {
    if (partial && partial !== QB_CATEGORY_UNCHANGED) {
      categories.add(category);
      partials.push(partial);
    }
  }

  return { categories, partials };
}

/**
 * 按类别计算 partial diff；deps 由调用方显式传入，等价于内联 useMemo 的依赖数组。
 *
 * - prev 读 `prevRef.current`，**必须**在 deps 中包含 `prevVersion`，否则 commit 后 partial 会 stale。
 * - detect / buildNext 为稳定引用或由调用方 deps 覆盖的闭包字段，不单独列入 deps。
 */
function useCategoryPartial(
  prevRef: MutableRefObject<IQBSnapshot | null>,
  category: TQBCategory,
  buildNext: (prev: IQBSnapshot) => IQBSnapshot,
  detect: (prev: IQBSnapshot, next: IQBSnapshot) => IPartialQBChangeSet,
  deps: DependencyList,
): TCategoryPartialResult {
  return useMemo((): TCategoryPartialResult => {
    const prev = prevRef.current;
    if (!prev) {
      return null;
    }
    return computeCategoryPartial(category, prev, buildNext(prev), detect);
    /*
     * deps 由调用方传入，ESLint 无法静态推断其内容，会误报 exhaustive-deps；
     * 此处关闭规则，依赖完整性由各 useCategoryPartial 调用处的 deps 保证。
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useQBCategoryPartials(): IUseQBCategoryPartialsResult {
  const columnDimensions = useQueryColumnDimensions();
  const columnMetrics = useQueryColumnMetrics();
  const columnUserFilters = useQuerySelector(
    s => s.columnUserFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const hasUserFilters = useQuerySelector(s => !!s.hasUserFilters);
  const columnFilters = useQuerySelector(
    s => s.columnFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnRawColumns = useQueryColumnRawColumns();
  const mode = useQuerySelector(s => s.mode as TChartCalculateMode);

  const hasDateDimension = useMemo(
    () => detectDateDimensionsInList(columnDimensions),
    [columnDimensions],
  );

  const modeLabel = useMemo((): TQBMode => {
    return mode === ChartCalculateMode.RawColumn ? 'raw' : 'aggregation';
  }, [mode]);

  const nextSnapshot = useMemo(
    (): IQBSnapshot => ({
      columnDimensions,
      columnMetrics,
      columnRawColumns,
      columnFilters,
      columnUserFilters,
      hasUserFilters,
      hasDateDimension,
      mode: modeLabel,
    }),
    [
      columnDimensions,
      columnMetrics,
      columnRawColumns,
      columnFilters,
      columnUserFilters,
      hasUserFilters,
      hasDateDimension,
      modeLabel,
    ],
  );

  const prevSnapshotRef = useRef<IQBSnapshot | null>(null);
  const nextSnapshotRef = useRef(nextSnapshot);
  nextSnapshotRef.current = nextSnapshot;

  /**
   * 与 commit 同步的版本号：仅用于击穿 useMemo 缓存，不表示业务状态。
   * pipeline 在 setTimeout 内 commit 后，prev 已对齐当前 QB，但 columnMetrics 等 deps 可能未变；
   * 递增本字段触发 re-render，使各类 partial 按新 prev 得到 UNCHANGED，避免长期携带 metricRemoved 等过期 diff。
   */
  const [prevVersion, setPrevVersion] = useState(0);

  const dimensionPartial = useCategoryPartial(
    prevSnapshotRef,
    'dimension',
    prev => ({ ...prev, columnDimensions, hasDateDimension }),
    detectDimensionChanges,
    [columnDimensions, hasDateDimension, prevVersion],
  );

  const metricPartial = useCategoryPartial(
    prevSnapshotRef,
    'metric',
    prev => ({ ...prev, columnMetrics }),
    detectMetricChanges,
    [columnMetrics, prevVersion],
  );

  const compareTimePartial = useCategoryPartial(
    prevSnapshotRef,
    'compareTime',
    prev => ({ ...prev, columnFilters }),
    detectCompareTimeChanges,
    [columnFilters, prevVersion],
  );

  const userFilterPartial = useCategoryPartial(
    prevSnapshotRef,
    'userFilter',
    prev => ({ ...prev, columnUserFilters, hasUserFilters }),
    detectUserFilterChanges,
    [columnUserFilters, hasUserFilters, prevVersion],
  );

  const rawColumnPartial = useCategoryPartial(
    prevSnapshotRef,
    'rawColumn',
    prev => ({ ...prev, columnRawColumns, mode: modeLabel }),
    detectRawColumnChanges,
    [columnRawColumns, modeLabel, prevVersion],
  );

  const { categories, partials } = useMemo(
    () =>
      mergeCategoryPartials([
        ['dimension', dimensionPartial],
        ['metric', metricPartial],
        ['compareTime', compareTimePartial],
        ['userFilter', userFilterPartial],
        ['rawColumn', rawColumnPartial],
      ]),
    [
      dimensionPartial,
      metricPartial,
      compareTimePartial,
      userFilterPartial,
      rawColumnPartial,
    ],
  );

  /**
   * 冻结当前 QB 为下次 diff 的 prev；必须 freeze，否则 model 原地 mutate 会导致 rename 漏检。
   * setPrevVersion：commit 与 useMemo 解耦的补丁——ref 更新不会进入 useMemo deps，需靠版本号触发重算。
   */
  const commitPrevSnapshotRef = useRef((): void => {
    prevSnapshotRef.current = freezeQBSnapshot(nextSnapshotRef.current);
    setPrevVersion(v => v + 1);
  });

  return {
    nextSnapshot,
    categories,
    partials,
    commitPrevSnapshot: commitPrevSnapshotRef.current,
  };
}
